'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Compass, MapPin } from 'lucide-react';
import type { FormCampusOption, WebFormField } from './types';
import { offeredOptions, plain, same } from './logic';

/**
 * THE CAMPUS QUESTION
 * ===================
 * Seventeen of Woodside's forms ask it, all as the same fourteen-option
 * dropdown. A grid of glyph tiles put every campus on screen at once, which
 * read as a wall and pushed the rest of the form below the fold; this is the
 * site's own campus dropdown instead, the one the group finder uses, so the
 * question takes one line until somebody opens it.
 *
 * Congregations that share a building are shown together: Troy Español sits
 * indented under Troy, and both are picked separately. Anything the form
 * offers that is not a campus — "I don't have one yet!" — is a plain row at
 * the bottom, because it is an answer about not having a campus rather than a
 * campus.
 *
 * The selected campus keeps its own colours. Filling a dark pill and inverting
 * the glyph turned every campus mark into a white blob.
 */

interface Row {
  /** The answer as MP spells it, which is what gets written. */
  option: string;
  label: string;
  campus: FormCampusOption | null;
  nested: boolean;
}

/**
 * A sub-congregation drops the campus name it shares with its parent: under
 * Troy, "Troy Español" reads "Español". The row it sits under already says
 * Troy, and repeating it makes the list look like two campuses rather than
 * one campus with two services. Only the label changes — the answer written
 * to MP is still the option's own text, so reports keep reading "Troy
 * Español".
 */
function shortLabel(label: string, parent: string): string {
  const trimmed = label.trim();
  const prefix = parent.trim();
  if (!prefix || !trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return trimmed;
  const rest = trimmed
    .slice(prefix.length)
    .replace(/^[\s\-–—:·]+/, '')
    .trim();
  return rest || trimmed;
}

function buildRows(options: string[], campuses: FormCampusOption[]): { rows: Row[]; other: Row[] } {
  const byName = new Map(campuses.map((c) => [c.name.trim().toLowerCase(), c]));
  const matched: { option: string; campus: FormCampusOption }[] = [];
  const other: Row[] = [];

  for (const option of options) {
    const label = plain(option);
    const campus = byName.get(label.toLowerCase()) ?? null;
    if (campus) matched.push({ option, campus });
    else other.push({ option, label, campus: null, nested: false });
  }

  // One entry per building, its own congregations under it.
  const keyOf = (c: FormCampusOption) => (c.locationId ? `loc-${c.locationId}` : `cong-${c.id}`);
  const families = new Map<string, { option: string; campus: FormCampusOption }[]>();
  for (const m of matched) {
    const key = keyOf(m.campus);
    families.set(key, [...(families.get(key) ?? []), m]);
  }

  const groups = [...families.values()].flatMap((family) => {
    // A family is built by pushing members into it, so it always has one. An
    // empty one has no campus to name and is dropped rather than drawn blank.
    const primary = family.find((m) => m.campus.isPrimary !== false) ?? family[0];
    if (!primary) return [];
    const rest = family.filter((m) => m !== primary);
    return [{ primary, rest }];
  });

  // Alphabetical. Somebody looking for their campus is scanning for a name,
  // not reading MP's field order.
  const byLabel = (a: string, b: string) => a.localeCompare(b, 'en', { sensitivity: 'base' });
  groups.sort((a, b) => byLabel(plain(a.primary.option), plain(b.primary.option)));

  const rows: Row[] = [];
  for (const { primary, rest } of groups) {
    const parentLabel = plain(primary.option);
    rows.push({
      option: primary.option,
      label: parentLabel,
      campus: primary.campus,
      nested: false,
    });
    rest.sort((a, b) => byLabel(plain(a.option), plain(b.option)));
    for (const sub of rest) {
      rows.push({
        option: sub.option,
        label: shortLabel(plain(sub.option), parentLabel),
        campus: sub.campus,
        nested: true,
      });
    }
  }

  return { rows, other };
}

export function CampusSelect({
  field,
  value,
  onChange,
  campuses,
  invalid,
}: {
  field: WebFormField;
  value: string;
  onChange: (value: string) => void;
  campuses: FormCampusOption[];
  invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const { rows, other } = useMemo(
    () => buildRows(offeredOptions(field), campuses),
    [field, campuses]
  );
  const selected = useMemo(
    () => [...rows, ...other].find((r) => same(r.option, value)) ?? null,
    [rows, other, value]
  );

  // Outside click. composedPath, because this runs inside a shadow root.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.composedPath()[0] as Node | undefined;
      if (target && container.current && !container.current.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Open upwards when there is no room below.
  useEffect(() => {
    if (!open || !panel.current || !container.current) return;
    const el = panel.current;
    el.style.top = '100%';
    el.style.bottom = 'auto';
    el.style.marginTop = '6px';
    el.style.marginBottom = '0';
    const rect = container.current.getBoundingClientRect();
    const height = el.getBoundingClientRect().height;
    const below = window.innerHeight - rect.bottom;
    if (below < height + 8 && rect.top > below) {
      el.style.top = 'auto';
      el.style.bottom = '100%';
      el.style.marginTop = '0';
      el.style.marginBottom = '6px';
    }
  }, [open]);

  const choose = (option: string) => {
    setOpen(false);
    onChange(same(option, value) ? '' : option);
  };

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`field flex items-center gap-2.5 text-left font-semibold ${invalid ? '!border-rose ring-rose/20 ring-2' : ''}`}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center">
          {selected?.campus?.svgUrl ? (
            <img
              src={selected.campus.svgUrl}
              alt=""
              className="h-6 w-6 object-contain"
              draggable={false}
            />
          ) : selected ? (
            <Compass className="text-slate h-4 w-4" />
          ) : (
            <MapPin className="text-slate h-4 w-4" />
          )}
        </span>
        <span className={`min-w-0 flex-1 truncate ${selected ? 'text-navy' : 'text-slate'}`}>
          {selected ? selected.label : 'Choose a campus'}
        </span>
        <ChevronDown
          className={`text-slate h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          ref={panel}
          role="listbox"
          className="border-line bg-paper absolute left-0 z-50 max-h-[22rem] w-full overflow-y-auto rounded-2xl border p-2 shadow-lg"
        >
          {rows.map((row) => (
            <CampusRow
              key={row.option}
              row={row}
              selected={same(row.option, value)}
              onClick={() => choose(row.option)}
            />
          ))}
          {other.length > 0 && (
            <>
              <hr className="border-line mx-2 my-1.5" />
              {other.map((row) => (
                <CampusRow
                  key={row.option}
                  row={row}
                  selected={same(row.option, value)}
                  onClick={() => choose(row.option)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CampusRow({
  row,
  selected,
  onClick,
}: {
  row: Row;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl text-left transition-colors ${
        row.nested ? 'py-2 pr-2 pl-12' : 'px-2 py-2.5'
      } ${selected ? 'bg-teal-soft' : 'hover:bg-mist'}`}
    >
      {!row.nested && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center">
          {row.campus?.svgUrl ? (
            <img
              src={row.campus.svgUrl}
              alt=""
              className="h-7 w-7 object-contain"
              draggable={false}
            />
          ) : (
            <Compass className="text-slate h-4 w-4" />
          )}
        </span>
      )}
      <span
        className={`min-w-0 flex-1 font-semibold ${row.nested ? 'text-[0.8rem]' : 'text-sm'} ${
          selected ? 'text-teal-deep' : 'text-navy'
        }`}
      >
        {row.label}
      </span>
      {selected && <Check className="text-teal-deep h-4 w-4 shrink-0" strokeWidth={3} />}
    </button>
  );
}
